<template>
  <div class="kbWhite px-6"
       :class="[isActionNested ? 'pt-0 pl-12 pb-6' : 'pt-6', actionType !== 'wait' ? 'pb-6' : null]"
       :style="{ borderLeft: isActionNested ? '1px solid #C4C4C4' : null }"
       style="border-radius: 6px">
    <div class="d-flex justify-space-between align-center mb-2">
      <div class="pb-1">
        <span class="kbDarkBlueText--text text-h6 pr-2" v-show="!isActionNested">
          {{ actionIndex + 1 }}.
        </span>

        <span class="kbDarkBlueText--text text-h6 ">
          {{ isActionNested ? $t('general.nestedActionToExecute') : $t('general.actionToExecute') }}
        </span>
      </div>

      <span v-if="!isActionNested">
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn color="kbRed"
                   icon
                   small
                   v-on="on"
                   @click="deleteAction()">
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('general.deleteAction') }}</span>
        </v-tooltip>
      </span>
    </div>

    <v-row>
      <v-col cols="12" class="py-0">
        <v-select background-color="kbLight"
                  color="kbDarkBlueText"
                  :items="actions"
                  item-text="name"
                  item-value="value"
                  :label="$t('general.pickAnAction')"
                  :rules="[rules.required]"
                  solo
                  validate-on-blur
                  v-model="localeActionType" />
      </v-col>

      <v-col cols="12" class="pt-0" v-if="isActionNested">
        <span class="kbDarkBlueText--text text-h6">
          {{ $t('general.timeToWaitBeforeNestedActionGetsExecuted') }}
        </span>

        <TimeMeasure inputs-background="kbLight"
                     :time="localeWaitFor"
                     :time-units="localeTimeUnits"
                     @onTimeChange="localeWaitFor = $event"
                     @onTimeUnitsChange="localeTimeUnits = $event"
        />

        <v-divider v-show="localeActionType" />
      </v-col>

      <v-col cols="12"
             class="py-0"
             v-if="actionType === 'changeSequence'">
        <div>
          <span class="kbDarkBlueText--text text-h6">
            {{ $t('general.pickTheSequenceToChangeTo') }}
          </span>
          <v-autocomplete background-color="kbLight"
                          color="kbDarkBlueText"
                          :items="filteredSequences"
                          item-text="name"
                          item-value="id"
                          :label="$t('general.pickTheSequenceToChangeTo')"
                          :rules="[rules.required]"
                          :search-input="searchSequenceId"
                          solo
                          validate-on-blur
                          :value="actionPayload.sequenceId"
                          @change="changeActionPayload({ key: 'sequenceId', value: $event })" />
        </div>

        <div v-show="actionPayload.sequenceId && sequenceSteps.length > 0">
          <span class="kbDarkBlueText--text text-h6">
            {{ $t('general.pickTheStepToChangeTo') }}
          </span>
          <v-select background-color="kbLight"
                    color="kbDarkBlueText"
                    :label="$t('general.pickAStep')"
                    :items="sequenceSteps"
                    item-text="name"
                    item-value="id"
                    solo
                    :value="actionPayload.stepId"
                    @change="changeActionPayload({ key: 'stepId', value: $event })" />
        </div>
      </v-col>

      <v-col cols="12"
             class="py-0"
             v-else-if="actionType === 'task'">
        <CreateTask :task="actionPayload" />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import CreateTask from '@/pages/App/Sequences/widgets/CreateTask'
import { mapState } from 'vuex'
import notifications from '@/libs/notifications'
import rules from '@/libs/rules'
import TimeMeasure from '@/pages/App/Sequences/widgets/TimeMeasure'
export default {
  components: { TimeMeasure, CreateTask },
  computed: {
    ...mapState({
      sequences: state => state.sequences.sequences
    }),
    actions () {
      const actions = [
        { name: this.$t('general.changeUserFromSequence'), value: 'changeSequence' },
        { name: this.$t('general.createTask'), value: 'task' },
        { name: this.$t('general.removeUserFromSequence'), value: 'finish' },
        { name: this.$t('general.doNothing'), value: 'nothing' },
        { name: this.$t('general.executeAnotherActionInAGivenTime'), value: 'wait' }
      ]
      if (this.isActionNested) {
        return actions.filter(item => item.value !== 'wait')
      } else {
        return actions
      }
    },
    filteredSequences () {
      if (this.selectedRuleSequenceId) return this.sequences.filter(item => item.id !== this.selectedRuleSequenceId)
      return this.sequences
    },
    localeActionType: {
      get () {
        return this.actionType
      },
      set (value) {
        this.$emit('changeActionType', value)
      }
    },
    localeTimeUnits: {
      get () {
        return this.timeUnits
      },
      set (value) {
        this.$emit('onTimeUnitsChange', value)
      }
    },
    localeWaitFor: {
      get () {
        return this.waitFor
      },
      set (value) {
        this.$emit('changeWaitFor', value)
      }
    }
  },
  data () {
    return {
      rules,
      searchSequenceId: '',
      sequenceSteps: []
    }
  },
  methods: {
    changeActionPayload ({ key, value }) {
      this.$emit('changeActionPayload', { key, value })
      if (key === 'sequenceId' && value) {
        this.getSequenceSteps()
      }
    },
    deleteAction () {
      this.$emit('deleteAction')
    },
    async getSequenceSteps () {
      try {
        this.sequenceSteps = await this.$store.dispatch('getSequenceSteps',
          {
            appId: this.$store.getters.appUuid,
            sequenceId: this.actionPayload.sequenceId,
            setStepsLocally: true
          }
        )
      } catch (e) {
        this.sequenceSteps = []
        notifications.error(e)
      }
    }
  },
  props: {
    actionIndex: {
      type: Number,
      default: -1
    },
    actionPayload: {
      type: Object,
      default: () => {}
    },
    actionType: {
      type: String,
      default: ''
    },
    isActionNested: {
      type: Boolean,
      default: false
    },
    selectedRuleSequenceId: {
      type: String,
      default: ''
    },
    timeUnits: {
      type: String,
      default: 'minutes'
    },
    waitFor: {
      type: [Number, String],
      default: 0
    }
  }
}
</script>
